import API from '../config/api';
import React, { useEffect, useState, useCallback } from 'react';

import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  ProgressBar,
  Spinner,
  Modal,
  Form
} from 'react-bootstrap';

import DeliveryNavbar from '../components/DeliveryNavbar';
const NEXT_STATUS = {
  Processing: 'Shipped',
  Shipped: 'Out For Delivery',
  'Out For Delivery': 'Delivered'
};

function DeliveryDashboard() {
  const navigate = useNavigate();

  const partner = (() => {
    try {
      return JSON.parse(localStorage.getItem('deliveryInfo'));
    } catch {
      return null;
    }
  })();

  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  /* Order awaiting delivery-OTP confirmation + the entered code */
  const [otpOrder, setOtpOrder] = useState(null);
  const [otpValue, setOtpValue] = useState('');
  const [otpSubmitting, setOtpSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!partner?._id) return;
    try {
      const [o, s] = await Promise.all([
        axios.get(`${API}/delivery/orders/${partner._id}`),
        axios.get(`${API}/delivery/stats/${partner._id}`)
      ]);
      setOrders(o.data);
      setStats(s.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [partner?._id]);

  useEffect(() => {
    load();
  }, [load]);

  const advanceStatus = async (order) => {
    const next = NEXT_STATUS[order.status];
    if (!next) return;

    /* Delivered needs the customer's OTP — open the verification modal. */
    if (next === 'Delivered') {
      setOtpOrder(order);
      setOtpValue('');
      return;
    }

    try {
      await axios.put(`${API}/orders/${order._id}`, { status: next });
      toast.success(`Marked as ${next}`);
      load();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const submitDeliveryOtp = async (e) => {
    e.preventDefault();
    if (!otpOrder) return;

    setOtpSubmitting(true);
    try {
      await axios.put(`${API}/orders/${otpOrder._id}`, {
        status: 'Delivered',
        otp: otpValue.trim()
      });
      toast.success('Delivery confirmed ✓');
      setOtpOrder(null);
      setOtpValue('');
      load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid delivery OTP');
    } finally {
      setOtpSubmitting(false);
    }
  };

  const collectCod = async (order) => {
    try {
      await axios.put(`${API}/orders/${order._id}/cod`, {
        codCollected: true
      });
      toast.success('COD marked as collected');
      load();
    } catch (error) {
      toast.error('Failed to update COD');
    }
  };

  const logout = () => {
    localStorage.removeItem('deliveryInfo');
    navigate('/delivery/login');
  };

  const progress = (status) =>
    ({ Processing: 25, Shipped: 50, 'Out For Delivery': 75, Delivered: 100 })[
      status
    ] || 0;

  const statusBg = (status) =>
    status === 'Cancelled'
      ? 'danger'
      : status === 'Delivered'
        ? 'success'
          : status === 'Out For Delivery'
            ? 'info'
            : status === 'Shipped'
              ? 'primary'
              : 'warning';

  if (loading) {
    return (
      <>
        <DeliveryNavbar />
        <div className="dp-wrap">
          <Container className="text-center py-5">
            <Spinner animation="border" />
          </Container>
        </div>
      </>
    );
  }

  const statCards = [
    { icon: '📦', value: stats?.totalAssigned ?? 0, label: 'Assigned' },
    { icon: '🚚', value: stats?.pending ?? 0, label: 'Pending' },
    { icon: '✅', value: stats?.delivered ?? 0, label: 'Delivered' },
    { icon: '📅', value: stats?.deliveredToday ?? 0, label: 'Today' },
    { icon: '💵', value: stats?.codToCollect ?? 0, label: 'COD Left' },
    { icon: '🏆', value: `₹${stats?.earnings ?? 0}`, label: 'Earnings' }
  ];

  return (
    <>
      <DeliveryNavbar />

      <div className="dp-wrap">
        <Container>
        {/* Header */}
        <Card className="border-0 shadow-lg mb-4 dp-hero">
          <Card.Body className="p-4 p-md-5 d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h2 className="fw-bold mb-1">🛵 Delivery Partner</h2>
              <p className="mb-0 dp-hero-text">
                Welcome, {partner?.name} · {partner?.vehicleType || 'Partner'}
              </p>
            </div>
            <Button
              variant="light"
              className="fw-semibold rounded-pill px-4"
              onClick={logout}
            >
              Logout
            </Button>
          </Card.Body>
        </Card>

        {/* Stats */}
        <Row className="g-3 mb-4">
          {statCards.map((c) => (
            <Col xs={6} md={4} lg={2} key={c.label}>
              <Card className="border-0 shadow-sm text-center h-100 dp-stat">
                <Card.Body className="py-3">
                  <div className="dp-stat-icon">{c.icon}</div>
                  <div className="dp-stat-value">{c.value}</div>
                  <div className="dp-stat-label">{c.label}</div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <h5 className="fw-bold mb-3">📋 My Assigned Orders</h5>

        {orders.length === 0 ? (
          <Card className="border-0 shadow-sm text-center py-5">
            <Card.Body>
              <div style={{ fontSize: '3rem' }}>📭</div>
              <h5 className="fw-bold mt-2">No Orders Assigned Yet</h5>
              <p className="text-muted mb-0">
                Orders assigned to you by the admin will appear here.
              </p>
            </Card.Body>
          </Card>
        ) : (
          <Row className="g-4">
            {orders.map((order) => (
              <Col lg={6} key={order._id}>
                <Card className="border-0 shadow-sm h-100 dp-card">
                  <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-bold">
                        Order #{order._id.slice(-6)}
                      </span>
                      <Badge
                        bg={statusBg(order.status)}
                        className="rounded-pill"
                      >
                        {order.status}
                      </Badge>
                    </div>

                    <ProgressBar
                      now={progress(order.status)}
                      variant={
                        order.status === 'Delivered' ? 'success' : 'warning'
                      }
                      style={{ height: '8px', borderRadius: '8px' }}
                      className="mb-3"
                    />

                    <div className="dp-info mb-2">
                      <div className="dp-label">Customer</div>
                      <div className="fw-semibold">{order.customerName}</div>
                      <a href={`tel:${order.phone}`} className="dp-phone">
                        📞 {order.phone}
                      </a>
                    </div>

                    <div className="dp-info mb-2">
                      <div className="dp-label">Deliver To</div>
                      <div>{order.address}</div>
                      <div className="text-muted">
                        {order.city} - {order.pincode}
                      </div>
                    </div>

                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {order.orderItems?.map((item, i) => (
                        <Badge
                          key={i}
                          bg="light"
                          text="dark"
                          className="px-2 py-1 rounded-pill"
                        >
                          {item.name} × {item.quantity}
                        </Badge>
                      ))}
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="fw-bold text-success fs-5">
                        ₹{order.total}
                      </span>
                      {order.paymentId ? (
                        <Badge bg="success" className="rounded-pill">
                          Paid Online
                        </Badge>
                      ) : order.codCollected ? (
                        <Badge bg="success" className="rounded-pill">
                          COD Collected
                        </Badge>
                      ) : (
                        <Badge bg="warning" className="rounded-pill">
                          COD Pending
                        </Badge>
                      )}
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                      {NEXT_STATUS[order.status] && (
                        <Button
                          size="sm"
                          variant="warning"
                          className="fw-semibold flex-fill"
                          onClick={() => advanceStatus(order)}
                        >
                          Mark {NEXT_STATUS[order.status]}
                        </Button>
                      )}

                      {!order.paymentId &&
                        !order.codCollected &&
                        order.status !== 'Cancelled' && (
                          <Button
                            size="sm"
                            variant="outline-success"
                            className="fw-semibold flex-fill"
                            onClick={() => collectCod(order)}
                          >
                            💵 Collect COD
                          </Button>
                        )}

                      {order.status === 'Cancelled' && (
                        <Badge
                          bg="danger"
                          className="align-self-center px-3 py-2 rounded-pill"
                        >
                          ✖ Cancelled by customer
                        </Badge>
                      )}

                      {order.status === 'Delivered' && (
                        <Badge
                          bg="success"
                          className="align-self-center px-3 py-2 rounded-pill"
                        >
                          ✓ Completed
                        </Badge>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        </Container>

        <Modal
          show={!!otpOrder}
          onHide={() => !otpSubmitting && setOtpOrder(null)}
          centered
        >
          <Form onSubmit={submitDeliveryOtp}>
            <Modal.Header closeButton>
              <Modal.Title>🔐 Confirm Delivery</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <p className="text-muted mb-3">
                Ask the customer for the OTP sent to their email and enter it
                below to mark order{' '}
                <strong>#{otpOrder?._id?.slice(-6)}</strong> as delivered.
              </p>

              <Form.Control
                type="text"
                inputMode="numeric"
                autoFocus
                placeholder="Enter 4-digit OTP"
                value={otpValue}
                onChange={(e) =>
                  setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 4))
                }
                className="text-center fw-bold"
                style={{ letterSpacing: '0.4rem', fontSize: '1.4rem' }}
              />
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setOtpOrder(null)}
                disabled={otpSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="success"
                disabled={otpValue.length < 4 || otpSubmitting}
              >
                {otpSubmitting ? 'Verifying…' : 'Confirm Delivery ✓'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <style>{dpStyles}</style>
      </div>
    </>
  );
}

const dpStyles = `
  .dp-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(20px, 4vw, 44px) 0;
  }
  .dp-hero {
    border-radius: 22px;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #7c2d12 60%, #ea580c);
  }
  .dp-hero-text { opacity: 0.9; }

  .dp-stat { border-radius: 16px; transition: transform 0.2s ease; }
  .dp-stat:hover { transform: translateY(-4px); }
  .dp-stat-icon { font-size: 1.5rem; }
  .dp-stat-value { font-size: 1.3rem; font-weight: 800; color: #b45309; }
  .dp-stat-label {
    font-size: 0.72rem; text-transform: uppercase;
    letter-spacing: 0.4px; color: #94a3b8; font-weight: 700;
  }

  .dp-card { border-radius: 16px; transition: transform 0.2s ease, box-shadow 0.2s ease; }
  .dp-card:hover { transform: translateY(-4px); box-shadow: 0 20px 36px rgba(194,65,12,0.15) !important; }
  .dp-info { background: #f8fafc; border-radius: 10px; padding: 8px 12px; }
  .dp-label {
    font-size: 0.66rem; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.4px; color: #94a3b8;
  }
  .dp-phone { color: #ea580c; text-decoration: none; font-weight: 600; font-size: 0.9rem; }

  @media (max-width: 575.98px) {
    .dp-stat-value { font-size: 1.05rem; }
  }
`;

export default DeliveryDashboard;
